export interface IOrganisation {
  APIKey: string;
  Addresses: Array<Object>;
  BaseCurrency: string;
  Class: string;
  CountryCode: string;
  CreatedDateUTC: string;
  DefaultPurchasesTax: string;
  DefaultSalesTax: string;
  Edition: string;
  ExternalLinks: Array<Object>;
  FinancialYearEndDay: number;
  FinancialYearEndMonth: number;
  IsDemoCompany: boolean;
  LegalName: string;
  Name: string;
  OrganisationEntityType: string;
  OrganisationID: string;
  OrganisationStatus: string;
  OrganisationType: string;
  PaymentTerms: Object;
  PaysTax: boolean;
  Phones: Array<string>;
  SalesTaxBasis: string;
  ShortCode: string;
  Timezone: string;
  Version: string;
}

export interface IXeroOrganisation {
  name: string;
  tenant_id: string;
  /** Xero's own org shortcode, the only way to deep-link into that org. */
  short_code: string | null;
}

/** GET /state — always 200, `organisation` is null when not connected. */
export interface IXeroState {
  /**
   * A live grant. False on an expired row. A `wrong_org` row still answers
   * true, and still cannot push: see `wrong_org`.
   */
  connected: boolean;
  /**
   * There is a credential row, but its refresh token is dead: the service
   * answers `connected: false, expired: true`, so `expired` is what says the row
   * exists at all. The user reconnects to revive it.
   */
  expired?: boolean;
  /** Connected to a Xero organisation other than the one this site is pinned to. */
  wrong_org?: boolean;
  organisation?: IXeroOrganisation | null;
  email?: string | null;
  connected_at?: string | null;
  last_used_at?: string | null;
  message?: string;
}

export interface IXeroConnectUrl {
  url: string;
}

export type XeroPushKind = "companies" | "invoices" | "quotes";

/** Singular, plural. Shared by the snackbars and the push results drawer. */
const XERO_PUSH_NOUNS: Record<XeroPushKind, [string, string]> = {
  companies: ["company", "companies"],
  invoices: ["invoice", "invoices"],
  quotes: ["quote", "quotes"],
};

export const xeroPushNoun = (kind: XeroPushKind, count: number) =>
  XERO_PUSH_NOUNS[kind][count === 1 ? 0 : 1];

/** Ids per POST. The service rejects anything larger with a 400. */
export const XERO_PUSH_CHUNK: Record<XeroPushKind, number> = {
  companies: 200,
  invoices: 50,
  quotes: 50,
};

/** POST /{kind}/push — one row per requested id, in request order. */
export interface IXeroPushRow {
  id: number;
  ok: boolean;
  xero_id: string | null;
  error: string | null;
  /** Already in Xero: nothing was sent for this record. */
  skipped?: boolean;
  batch?: string | null;
  warning?: string;
  /** The service also writes "unknown" when it cannot tell (api.py:68,529). */
  journal?: "ok" | "failed" | "skipped" | "unknown" | null;
}

export interface IXeroPushResult {
  batch_id: string | null;
  results: IXeroPushRow[];
}

/** What the hook hands back after chunking: one merged result plus counts. */
export interface IXeroPushOutcome {
  kind: XeroPushKind;
  batches: string[];
  /**
   * How many ids the user asked to push. A failed chunk stops the push, so this
   * is not always `results.length`: the difference is the ids that never went
   * up, and the drawer has to say so.
   */
  requested: number;
  results: IXeroPushRow[];
  ok: number;
  failed: number;
  skipped: number;
}

/**
 * A background sync job. Both kinds are batch rows with a cursor over an
 * ordered id list, so `/batches` lists them beside the user-clicked pushes.
 */
export type XeroSyncKind = "company_sync" | "contact_import";

/** Which companies a company_sync covers. */
export type XeroSyncScope = "all" | "unlinked";

/**
 * `stalled` is derived on read, not stored: an open job whose `updated_at` is
 * older than the service's stall window. It reads as running but is not, so it
 * is resumable and does not block a new job.
 */
export type XeroSyncStatus = "open" | "complete" | "failed" | "stalled";

/**
 * GET /companies/sync/{code} and GET /contacts/sync/{code}. One shape for both
 * kinds: a company sync counts records (`ok`/`skipped`), a contact import counts
 * Xero pages and rows (`pages_done`/`imported`/`updated`), and `failed` is the
 * only count both keep.
 */
export interface IXeroSyncJob {
  code: string;
  kind: XeroSyncKind;
  status: XeroSyncStatus;
  /**
   * company_sync only: how many records the job is for. A contact import never
   * has one — Xero does not say how many pages there are — so the browser reads
   * both of these as absent rather than zero.
   */
  total?: number;
  /** company_sync only: how far down `total` the job has got. */
  cursor?: number;
  failed: number;
  started_at: string | null;
  updated_at: string | null;
  finished_at: string | null;
  /** Why the job failed. Null on every other status. */
  last_error: string | null;
  pushed_by: string | null;
  /** company_sync only. */
  scope?: XeroSyncScope | null;
  ok?: number;
  /** Unchanged since the last sync, so nothing was sent for it. */
  skipped?: number;
  /** contact_import only: the modified-since filter the job reads Xero with. */
  since?: string | null;
  pages_done?: number;
  imported?: number;
  updated?: number;
  /**
   * company_sync only: the companies that failed where their own record could
   * not say so. A record that already holds a Xero link is never overwritten
   * with a failure, so for those this is the only account of what went wrong.
   */
  failures?: IXeroSyncFailure[];
}

/** One company a sync could not push, and what was said about it. */
export interface IXeroSyncFailure {
  id: number;
  error: string;
}

/**
 * POST /companies/sync, /contacts/sync and either resume route — 202.
 *
 * A start route answers these three keys. A resume answers the whole job view
 * with `job` alongside the view's own `code`, so reading `job` covers both.
 */
export interface IXeroSyncStart {
  job: string;
  status?: XeroSyncStatus;
  total?: number;
  /** The job view's own name for the code, present on a resume. */
  code?: string;
}

/** 409 from either start route: a job is already running, and this is its code. */
export interface IXeroSyncConflict {
  error: string;
  job?: string;
}

/** GET /batches?limit= — user-clicked pushes and background jobs in one list. */
export interface IXeroBatch {
  code: string;
  kind: "invoice" | "quote" | "company" | XeroSyncKind;
  uid: string;
  pushed_by: string | null;
  status: "open" | "complete" | "failed" | "aborted" | "stalled";
  total: number;
  ok: number;
  failed: number;
  skipped: number;
  created_at: string;
  completed_at?: string;
  /** Job rows only: how far down `total` the job has got. */
  cursor?: number;
  scope?: XeroSyncScope | null;
  last_error?: string | null;
}

/**
 * Batch kinds are singular; the push routes and the copy are plural. A job kind
 * has no push route: it is resumed, not re-pushed.
 */
const XERO_BATCH_KINDS: Record<
  IXeroBatch["kind"],
  [string, XeroPushKind | undefined]
> = {
  invoice: ["Invoices", "invoices"],
  quote: ["Quotes", "quotes"],
  company: ["Companies", "companies"],
  company_sync: ["Company sync", undefined],
  contact_import: ["Contact import", undefined],
};

export const xeroBatchKindLabel = (kind: IXeroBatch["kind"]) =>
  XERO_BATCH_KINDS[kind]?.[0] ?? kind;

/** Which push route a retry of this batch goes down. Jobs have none. */
export const xeroBatchPushKind = (kind: IXeroBatch["kind"]) =>
  XERO_BATCH_KINDS[kind]?.[1];

/** A batch row that is a background job, so Resume applies rather than Retry. */
export const isXeroSyncKind = (
  kind: IXeroBatch["kind"]
): kind is XeroSyncKind =>
  kind === "company_sync" || kind === "contact_import";

/** Only a job that has stopped can be resumed. */
export const isXeroSyncResumable = (status: string) =>
  status === "failed" || status === "stalled";

/** GET /batches/{code} */
export interface IXeroBatchDetail extends IXeroBatch {
  records: IXeroBatchRecord[];
}

export interface IXeroBatchRecord {
  id: number;
  kind: string;
  batch: string | null;
  status: "pending" | "ok" | "unstamped" | "failed";
  xero_id: string | null;
  error: string | null;
  journal?: string | null;
  journal_error?: string | null;
  updated_at: string;
}

export interface IXeroMappingRow {
  key: string;
  value: string;
  name: string | null;
  source: "default" | "override";
  /** The code resolves to something that exists in the Xero organisation. */
  wired: boolean;
  item?: string;
  account_id?: number | null;
  category?: string | null;
  sub_category?: string | null;
  tax_code?: string | null;
  in_chart?: boolean;
  status?: string | null;
}

export interface IXeroChartSummary {
  total: number;
  revenue: number;
  expense: number;
  asset: number;
  liability?: number;
  equity?: number;
}

export interface IXeroAccountMappingResponse {
  items: IXeroMappingRow[];
  chart_summary: IXeroChartSummary;
}

export interface IXeroAccountMappingUpdate {
  mappings: Record<string, string>;
}

/** GET /config - Account mapping */
export interface IXeroConfig {
  mapping?: IXeroMappingRow[];
  missing?: string[];
  client_id?: string;
  is_enabled?: boolean;
}

/** Tenant app credentials configuration */
export interface IXeroTenantAppConfig {
  id?: number;
  company_id: number;
  client_id?: string;
  redirect_uri?: string;
  is_enabled: boolean;
  client_secret_masked?: string;
  has_client_secret: boolean;
  client_secret?: string;
  effective_redirect_uri?: string;
  credential_source?: string;
}

/**
 * A Xero contact as the service hands it over: Xero's own fields, plus whether
 * Aeronet already holds it as a company. ARCHIVED contacts are dropped upstream.
 */
export interface IXeroContact {
  ContactID: string;
  Name: string;
  EmailAddress: string | null;
  IsCustomer: boolean;
  IsSupplier: boolean;
  ContactStatus: string;
  UpdatedDateUTC: string;
  /** Already a company in Aeronet: nothing to import, nothing to select. */
  linked: boolean;
  company_id: number | null;
}

/**
 * GET /contacts?page= — one Xero page, 100 contacts. Xero gives no total, so
 * `has_more` is the only thing paging can be driven off.
 */
export interface IXeroContactsPage {
  page: number;
  contacts: IXeroContact[];
  has_more: boolean;
}

/** POST /contacts/import — one row per requested contact id. */
export interface IXeroContactImportRow {
  contact_id: string;
  ok: boolean;
  error: string | null;
}

export interface IXeroContactImportResult {
  created: number;
  updated: number;
  results: IXeroContactImportRow[];
}

/** GET /companies/summary */
export interface IXeroCompaniesSummary {
  linked: number;
  not_linked: number;
  last_import: {
    created: number;
    updated: number;
    at: string;
    by: string | null;
  } | null;
}
